import { memo, SyntheticEvent, useEffect, useState } from "react";
import {
  Button,
  Dimmer,
  Form,
  FormField,
  FormGroup,
  Icon,
  Input,
  Loader,
  Modal,
  Tab,
  TabPane,
  TabProps,
} from "semantic-ui-react";
import { useIntl } from "react-intl";

// Components - Queue
import CardServicesQueue from "react-lib/apps/MEH/CardServicesQueue";
import CardPatientQueue from "react-lib/apps/MEH/CardPatientQueue";

// Styles
import "./CardMeBillingMain.scss";

// serializer
import CentralLabOrderSimplifiedSerializer from "issara-sdk/types/CentralLabOrderSimplifiedSerializer_apps_LAB";
import CentralLabOrderItemActionLogSerializer from "issara-sdk/types/CentralLabOrderItemActionLogSerializer_apps_LAB";
import SpecimenContainerSerializer from "issara-sdk/types/SpecimenContainerSerializer_apps_LAB";

// Components
import SnackMessage from "react-lib/apps/common/SnackMessage";
import CardMeBillingDetail from "react-lib/apps/MEH/CardMeBillingDetail";
import MeGenRunView from "react-lib/apps/MeGen/MeGenRunView";
import CardMeBillingCustomerList from "react-lib/apps/MEH/CardMeBillingCustomerList";
import ModMeBillingCustomer from "react-lib/apps/MEH/ModMeBillingCustomer";

// Interface
import { SaveServiceOrderType } from "react-lib/apps/MEH/sequence/SaveServiceOrder";
import { MedicalFeeHistoryType } from "react-lib/apps/HISV3/BIL/sequence/MedicalFeeHistory";
import { PaymentSummaryType } from "react-lib/apps/MEH/sequence/PaymentSummary";
import { PaymentHistoryType } from "react-lib/apps/MEH/sequence/PaymentHistory";
import { State as PackagePurchaseState } from "react-lib/apps/HISV3/HCU/sequence/PackagePurchase";
import { State as PackageOrderState } from "react-lib/apps/HISV3/HCU/sequence/PackageOrder";
import { State as LabReportSequence } from "react-lib/apps/HISV3/LAB/sequence/LabReport";
import {
  LabOrderQueueType,
  SpecimenCollectorMasterDataType,
} from "react-lib/apps/HISV3/LAB/LabInterface";
import { State as OPDLabSummarySequence } from "react-lib/apps/HISV3/LAB/sequence/OPDLabSummary";
import { State as MakeAppointmentMeHeyState } from "react-lib/apps/MEH/sequence/MakeAppointmentMeHey";

// Types
import {
  RunSequence,
  SetProp,
  State as MeBillingState,
  OnEvent,
  PickedState,
  MAIN_CARD_NAME,
  GetCustomerListParams,
  CreateCustomerParams,
} from "./types/MeBillingType";
import { RowInfo } from "react-table-6";

type CardMePOSCRMProps = {
  controller: any;
  runSequence: RunSequence;
  setProp: SetProp;
  onEvent: OnEvent;

  MeBillingSequence: MeBillingState["MeBillingSequence"];

  SaveServiceOrderSequence?: SaveServiceOrderType;
  MedicalFeeHistorySequence?: MedicalFeeHistoryType;
  PaymentSummarySequence?: PaymentSummaryType;
  PaymentHistorySequence?: PaymentHistoryType;

  PackagePurchaseSequence?: PackagePurchaseState["PackagePurchaseSequence"];
  PackageOrderSequence?: PackageOrderState["PackageOrderSequence"];

  MakeAppointmentMeHeySequence?: MakeAppointmentMeHeyState["MakeAppointmentMeHeySequence"];

  MeGenSequence?: any;
  BillingAndPackageSequence?: any;

  goToMeGen?: { projectId: number; threadId?: string } | null;
  languageUX: "en" | "th";

  // Patient
  patientInfo?: Record<string, any>;
  selectedEncounter?: Record<string, any>;
  django?: Record<string, any>;

  // Invoice items
  invoiceItemByModes?: Record<string, any>[];
  invoiceItemByOrders?: Record<string, any>[];
  invoiceItemByItems?: Record<string, any>[];
  invoiceItemPackageList?: Record<string, any>[];

  // Orders
  patientOrders?: Record<string, any>[];
  refundOrders?: Record<string, any>;

  // Filters / Status
  medicalFeeFilter?: Record<string, any>;
  patientEncounterLockerStatus?: Record<string, any>[];
  encounterSuspension?: Record<string, any>;

  // Bill / Payment
  BillPaymentSequence?: Record<string, any> | null;
  billPendingDetail?: Record<string, any>;
  billReportPrint?: Record<string, any>;
  bilReceiptCodeDetail?: Record<string, any>;
  faxClaimItemList?: Record<string, any>;

  // Lab Order
  labOrderQueue?: LabOrderQueueType;
  selectedLabOrderWorking?: CentralLabOrderSimplifiedSerializer;
  specimenCollectorMasterData?: SpecimenCollectorMasterDataType;
  selectedContainer?: SpecimenContainerSerializer | null;
  specimenLogs?: CentralLabOrderItemActionLogSerializer[];

  // Lab Report
  LabReportSequence?: LabReportSequence["LabReportSequence"];
  permissionLabResultConfidential?: boolean;

  // Lab Summary
  OPDLabSummarySequence?: OPDLabSummarySequence["OPDLabSummarySequence"];
  selectedPatient?: Record<string, any>;
  centralLabResultList?: {
    items?: Record<string, any>[];
  };

  // Master / Task
  masterData?: Record<string, any[]>;
  taskTrackingList?: Record<string, any>;

  // Language
  billingPatientLanguage?: any;

  // Queue
  meBillingActiveTab?: number;
  meBillingInitialMenu?: string;

  // print list
  printListController?: Record<string, any>;
} & Pick<
  PickedState,
  | "buttonLoadCheck"
  | "errorMessage"
  | "successMessage"
  | "loadingStatus"
  | "masterOptions"
  | "selectedDivision"
  | "searchedItemListWithKey"
  | "qrCodePayment"
  | "userProfile"
>;

const CardMePOSCRM = (props: CardMePOSCRMProps) => {
  const intl = useIntl();

  const [activeTabIndex, setActiveTabIndex] = useState<number>(0);
  const [openCustomerMod, setOpenCustomerMod] = useState<boolean>(false);
  const [editCustomerData, setEditCustomerData] = useState<CreateCustomerParams | null>(null);

  useEffect(() => {
    if (props.meBillingActiveTab !== undefined) {
      setActiveTabIndex(props.meBillingActiveTab);
    }
  }, [props.meBillingActiveTab]);

  useEffect(() => {
    props.runSequence({
      sequence: "MeBilling",
      action: "GET_CUSTOMER_LIST",
    });
  }, [props.selectedDivision?.id]);

  const handleSearch = (filter: GetCustomerListParams) => {
    props.runSequence({
      sequence: "MeBilling",
      action: "GET_CUSTOMER_LIST",
      filter,
      activePage: 1,
    });
  };

  const handleChangePage = (activePage: number) => {
    props.runSequence({
      sequence: "MeBilling",
      action: "GET_CUSTOMER_LIST",
      activePage,
    });
  };

  const handleOpenNewCustomer = () => {
    setEditCustomerData(null);
    setOpenCustomerMod(true);
  };

  const handleEditCustomer = (customerData: CreateCustomerParams) => {
    setEditCustomerData(customerData);
    setOpenCustomerMod(true);
  };

  const handleCloseCustomerMod = () => {
    setOpenCustomerMod(false);
    setEditCustomerData(null);
  };

  const handleSaveCustomer = (data: CreateCustomerParams) => {
    const isEditMode = !!editCustomerData?.id;

    if (isEditMode && editCustomerData?.id) {
      props.runSequence({
        sequence: "MeBilling",
        action: "UPDATE_CUSTOMER",
        customerId: editCustomerData.id,
        customerData: data,
        onSuccess: handleCloseCustomerMod,
      });
    } else {
      props.runSequence({
        sequence: "MeBilling",
        action: "CREATE_CUSTOMER",
        customerData: data,
        onSuccess: handleCloseCustomerMod,
      });
    }
  };

  const handleCloseSnackMessage = () => {
    props.setProp(`errorMessage.${MAIN_CARD_NAME}`, null);
    props.setProp(`successMessage.${MAIN_CARD_NAME}`, null);
  };

  const handleClearSelectCustomer = () => {
    props.setProp(`MeBillingSequence`, {
      ...props.MeBillingSequence,
      selectedCustomer: undefined,
    });

    handleSearch({});
  };

  const handleSelectCustomer = (selectedRow: RowInfo["original"]) => {
    if (!selectedRow?.id) {
      console.warn("[SelectedCustomer] Customer not found!!");
      return;
    }

    if (!selectedRow?.proxy_patient) {
      console.warn("[SelectedCustomer] Proxy Patient not found!!");
      return;
    }

    props.runSequence({
      sequence: "MeBilling",
      action: "GET_CUSTOMER_DETAIL",
      customerId: selectedRow.id,
      proxyPatientId: selectedRow.proxy_patient,
    });
  };

  // console.log("Yeti [MeBillingMain] Props: ", props);

  const handleChangeTab = (_e: SyntheticEvent, data: TabProps) => {
    setActiveTabIndex(data.activeIndex as number);
  };

  const panes = [
    {
      menuItem: {
        key: "search_member",
        content: (
          <div style={{ alignItems: "center", width: "10rem", justifyContent: "center" }}>
            <label>Search Member</label>
          </div>
        ),
      },
      render: () => (
        <TabPane style={{ height: "100%", overflow: "auto", position: "relative" }}>
          <Dimmer active={props.loadingStatus?.[MAIN_CARD_NAME]} inverted>
            <Loader inverted>Loading</Loader>
          </Dimmer>

          <SnackMessage
            error={props.errorMessage?.[MAIN_CARD_NAME]}
            success={props.successMessage?.[MAIN_CARD_NAME]}
            onClose={handleCloseSnackMessage}
          />

          {props.MeBillingSequence?.selectedCustomer && (
            <CardMeBillingDetail
              controller={props.controller}
              selectedCustomer={props.MeBillingSequence?.selectedCustomer}
              visitList={props.MeBillingSequence?.visitList}
              selectedVisit={props.MeBillingSequence?.selectedVisit}
              appointmentList={props.MeBillingSequence?.appointmentList}
              packageDetail={props.MeBillingSequence?.packageDetail || []}
              providerList={props.MeBillingSequence?.common?.providerList || []}
              doctorList={props.MeBillingSequence?.common?.doctorList || []}
              onGoBack={handleClearSelectCustomer}
              initialMenu={props.meBillingInitialMenu}
              onEditCustomer={handleEditCustomer}
              SaveServiceOrderSequence={props.SaveServiceOrderSequence}
              runSequence={props.runSequence}
              onEvent={props.onEvent as any}
              setProp={props.setProp}
              languageUX={props.languageUX}
              buttonLoadCheck={props.buttonLoadCheck}
              errorMessage={props.errorMessage}
              successMessage={props.successMessage}
              loadingStatus={props.loadingStatus}
              selectedDivision={props.selectedDivision}
              searchedItemListWithKey={props.searchedItemListWithKey}
              userProfile={props.userProfile}
              patientInfo={props.patientInfo}
              selectedEncounter={props.selectedEncounter}
              invoiceItemByModes={props.invoiceItemByModes}
              invoiceItemByOrders={props.invoiceItemByOrders}
              invoiceItemByItems={props.invoiceItemByItems}
              patientOrders={props.patientOrders}
              refundOrders={props.refundOrders}
              medicalFeeFilter={props.medicalFeeFilter}
              patientEncounterLockerStatus={props.patientEncounterLockerStatus}
              encounterSuspension={props.encounterSuspension}
              BillPaymentSequence={props.BillPaymentSequence}
              masterOptions={props.masterOptions}
              taskTrackingList={props.taskTrackingList}
              billPendingDetail={props.billPendingDetail}
              bilReceiptCodeDetail={props.bilReceiptCodeDetail}
              billReportPrint={props.billReportPrint}
              faxClaimItemList={props.faxClaimItemList}
              invoiceItemPackageList={props.invoiceItemPackageList}
              billingPatientLanguage={props.billingPatientLanguage}
              qrCodePayment={props.qrCodePayment}
              MedicalFeeHistorySequence={props.MedicalFeeHistorySequence}
              PaymentSummarySequence={props.PaymentSummarySequence}
              PaymentHistorySequence={props.PaymentHistorySequence}
              PackagePurchaseSequence={props.PackagePurchaseSequence}
              PackageOrderSequence={props.PackageOrderSequence}
              django={props.django}
              selectedLabOrderWorking={props.selectedLabOrderWorking}
              selectedContainer={props.selectedContainer}
              specimenLogs={props.specimenLogs}
              specimenCollectorMasterData={props.specimenCollectorMasterData}
              labOrderQueue={props.labOrderQueue}
              LabReportSequence={props.LabReportSequence}
              permissionLabResultConfidential={props.permissionLabResultConfidential}
              OPDLabSummarySequence={props.OPDLabSummarySequence}
              selectedPatient={props.selectedPatient}
              centralLabResultList={props.centralLabResultList}
              MakeAppointmentMeHeySequence={props.MakeAppointmentMeHeySequence}
              printListController={props.printListController}
              // config
              isMePOSCRM={true}
            />
          )}

          {!props.MeBillingSequence?.selectedCustomer && (
            <CardMeBillingCustomerList
              setProp={props.setProp}
              customerList={props.MeBillingSequence?.customerList}
              onSelectCustomer={handleSelectCustomer}
              onSearch={handleSearch}
              onPageChange={handleChangePage}
              onOpenNewCustomer={handleOpenNewCustomer}
              buttonLoadCheck={props.buttonLoadCheck}
              loadingStatus={props.loadingStatus}
              languageUX={props.languageUX}
              // config
              isHideButtonNewCustomer={true}
              isShowFilterMobileNumber={true}
            />
          )}
        </TabPane>
      ),
    },
  ];

  return (
    <div className="card-mebilling-main">
      <Tab
        activeIndex={activeTabIndex}
        menu={{ pointing: true, secondary: true }}
        panes={panes}
        onTabChange={handleChangeTab}
      />

      {/* จอ run ของ MeGen — เปิดจากปุ่มในใบผลแลป (LabReport: GOTO_MEGEN_PROJECT ตั้ง goToMeGen) */}
      <Modal
        open={!!props.goToMeGen}
        size="fullscreen"
        closeIcon
        onClose={() => props.setProp("goToMeGen", null)}
      >
        <Modal.Content style={{ height: "93vh", overflow: "auto", padding: 0 }}>
          {!!props.goToMeGen && (
            <MeGenRunView
              MeGenSequence={props.MeGenSequence}
              onEvent={props.onEvent}
              setProp={props.setProp}
              runSequence={props.runSequence}
              BillingAndPackageSequence={props.BillingAndPackageSequence}
              selectedDivisionId={props.selectedDivision?.id}
              defaultFinalOutput
              defaultAudioOnly={false}
              projectId={props.goToMeGen.projectId}
              threadId={props.goToMeGen.threadId || null}
              onNavigateBack={() => props.setProp("goToMeGen", null)}
            />
          )}
        </Modal.Content>
      </Modal>
    </div>
  );
};

CardMePOSCRM.displayName = "CardMePOSCRM";

export default memo(CardMePOSCRM);
